import { isReplayableRequest } from './nonReplayableEndpoints';

/**
 * Information about a failed request that can be retried
 */
export interface FailedRequest {
    url: string;
    options: RequestInit;
}

/**
 * API status type
 */
export type ApiStatus = 'up' | 'down' | 'maintenance';

/**
 * Manager for API status tracking
 * Allows non-React code (like BaseApiService) to notify React components about API status
 */
class ApiStatusManager {
    private listeners: Set<(status: ApiStatus) => void> = new Set();
    private apiStatus: ApiStatus = 'up';
    private lastFailedRequest: FailedRequest | null = null;

    /**
     * Subscribe to API status changes
     * @param callback - Function to call when API status changes
     * @returns Unsubscribe function
     */
    subscribe(callback: (status: ApiStatus) => void): () => void {
        this.listeners.add(callback);
        // Immediately notify the new subscriber of current status
        callback(this.apiStatus);

        return () => {
            this.listeners.delete(callback);
        };
    }

    /**
     * Set the API down status and notify all subscribers
     * @param isDown - Whether the API is down (connection error)
     */
    setApiDown(isDown: boolean): void {
        const newStatus: ApiStatus = isDown ? 'down' : 'up';
        if (this.apiStatus !== newStatus) {
            this.apiStatus = newStatus;
            this.listeners.forEach(callback => callback(newStatus));
        }
    }

    /**
     * Set the API maintenance status (503 response)
     * @param isMaintenance - Whether the API is under maintenance
     */
    setMaintenance(isMaintenance: boolean): void {
        const newStatus: ApiStatus = isMaintenance ? 'maintenance' : 'up';
        if (this.apiStatus !== newStatus) {
            this.apiStatus = newStatus;
            this.listeners.forEach(callback => callback(newStatus));
        }
    }

    /**
     * Get the current API status
     * @returns The current API status
     */
    getApiStatus(): ApiStatus {
        return this.apiStatus;
    }

    /**
     * Get whether the API is down (for backward compatibility)
     * @returns Whether the API is down (connection error)
     */
    getApiDown(): boolean {
        return this.apiStatus === 'down';
    }

    /**
     * Store the last failed request for retry
     * @param request - The failed request information
     */
    setLastFailedRequest(request: FailedRequest | null): void {
        this.lastFailedRequest = request;
    }

    /**
     * Get the last failed request
     * @returns The last failed request, or null if none
     */
    getLastFailedRequest(): FailedRequest | null {
        return this.lastFailedRequest;
    }

    /**
     * Retry the last failed request
     * @returns Promise containing the response, or null if no request to retry
     */
    async retryLastRequest(): Promise<Response | null> {
        if (!this.lastFailedRequest) {
            return null;
        }

        // Defence in depth: BaseApiService already refuses to store these, but this manager is a
        // singleton anything can populate, and replaying a payment POST charges the card twice.
        if (!isReplayableRequest(this.lastFailedRequest.url)) {
            this.setLastFailedRequest(null);
            return null;
        }

        try {
            const response = await fetch(this.lastFailedRequest.url, this.lastFailedRequest.options);
            // Check response status
            if (response.status === 503) {
                this.setMaintenance(true);
            } else if (response.ok) {
                // If successful, API is back up
                this.setApiDown(false);
                this.setMaintenance(false);
                this.setLastFailedRequest(null);
            }
            return response;
        } catch (error) {
            // Still down, keep the request stored for another retry
            throw error;
        }
    }
}

// Export singleton instance
export const apiStatusManager = new ApiStatusManager();
